import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import AudioAssetsList from '../components/audio-assets/AudioAssetsList';
import AudioPromptBox from '../components/audio-assets/AudioPromptBox';
import Pagination from '../components/ui/Pagination';
import { useAudioAssetGeneration } from '../hooks/useAudioAssetGeneration';
import { useGetFeaturedAudioAssets, useGetAudioAssets, useDeleteAudioAsset } from '../http-hooks/audio-assets/useAudioAssets';
import useIsMobile from '../hooks/useIsMobile';
import { C } from '../styles/tokens';

type TabType = 'explore' | 'my-assets';
type AssetType = 'sound-effect' | 'music';

interface AudioAssetsProps {
  type: AssetType;
  title: string;
  icon?: React.ReactNode;
}

const ITEMS_PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 300;

// Suggestion chips shown above the prompt widget — clicking one prefills the prompt.
const SUGGESTION_CHIPS: Record<AssetType, string[]> = {
  'sound-effect': ['Thunder rolling in distance', 'Sword clash with ring', 'Sci-fi door hiss'],
  music: ['Upbeat synthwave', 'Acoustic folk ballad', 'Dark ambient soundscape'],
};

const AudioAssets: React.FC<AudioAssetsProps> = ({ type, title }) => {
  const { org_id, id: integrationId } = useParams<{ org_id: string; id: string }>();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [activeTab, setActiveTab] = useState<TabType>('explore');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Lifted prompt value — shared by the suggestion chips, the "regenerate"
  // row action, and the prompt widget itself (controlled).
  const [promptValue, setPromptValue] = useState('');
  const promptBoxRef = useRef<HTMLDivElement>(null);

  // Pagination state for explore tab
  const [explorePageTokens, setExplorePageTokens] = useState<(string | null)[]>([null]);
  const [exploreCurrentPage, setExploreCurrentPage] = useState(0);

  // Pagination state for my-assets tab
  const [myAssetsPageTokens, setMyAssetsPageTokens] = useState<(string | null)[]>([null]);
  const [myAssetsCurrentPage, setMyAssetsCurrentPage] = useState(0);

  // Debounce search query
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      // Reset pagination when search changes
      setExplorePageTokens([null]);
      setExploreCurrentPage(0);
      setMyAssetsPageTokens([null]);
      setMyAssetsCurrentPage(0);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Use unified hook for audio asset generation with WebSocket support
  const { isGenerating, generateAudioAsset } = useAudioAssetGeneration(
    org_id,
    integrationId,
    type,
    (result) => {
      // On success, switch to my assets tab and invalidate cache
      console.log('Audio asset generated:', result);
      queryClient.invalidateQueries(['audio-assets', type, org_id, integrationId]);
      // Reset to first page when new asset is generated
      setMyAssetsPageTokens([null]);
      setMyAssetsCurrentPage(0);
      handleTabChange('my-assets');
      // Toast is already shown by the hook
    }
  );

  // Fetch featured assets with pagination and search
  const {
    data: featuredResponse,
    isLoading: featuredLoading,
    error: featuredError,
    isFetching: featuredFetching
  } = useGetFeaturedAudioAssets({
    type,
    limit: ITEMS_PER_PAGE,
    next_page_token: explorePageTokens[exploreCurrentPage],
    search: debouncedSearchQuery
  });

  // Fetch user's assets with pagination and search
  const {
    data: myAssetsResponse,
    isLoading: myLoading,
    error: myError,
    isFetching: myFetching
  } = useGetAudioAssets({
    org_id: org_id || '',
    integration_id: integrationId || '',
    type: type,
    limit: ITEMS_PER_PAGE,
    next_page_token: myAssetsPageTokens[myAssetsCurrentPage],
    search: debouncedSearchQuery
  });

  const deleteMutation = useDeleteAudioAsset();

  // Handle URL tab parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'my-assets') {
      setActiveTab('my-assets');
    } else {
      setActiveTab('explore');
    }
  }, [location.search]);

  // Reset pagination when tab changes
  useEffect(() => {
    if (activeTab === 'explore') {
      setExplorePageTokens([null]);
      setExploreCurrentPage(0);
    } else {
      setMyAssetsPageTokens([null]);
      setMyAssetsCurrentPage(0);
    }
  }, [activeTab]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);

    const baseUrl = `/organisation/${org_id}/integration/${integrationId}/${type === 'sound-effect' ? 'sound-effects' : 'music'}`;
    if (tab === 'explore') {
      history.push(baseUrl);
    } else {
      history.push(`${baseUrl}?tab=${tab}`);
    }
  };

  const handleGenerate = async (prompt: string, settings: any) => {
    // GROW-85: the old localStorage-based "Feature - First Used" proxy was removed.
    // Activation is now measured by the server-side first-value event (FIRST_KEY_ACTION),
    // fired once per customer on first successful audio. Music/SFX first-use coverage
    // is picked up when those tools get the generation contract in GROW-84.
    await generateAudioAsset(prompt, settings);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync({
        org_id: org_id || '',
        integration_id: integrationId || '',
        asset_id: id,
        type: type
      });
      const typeName = type === 'sound-effect' ? 'Sound effect' : 'Music';
      toast.success(`${typeName} deleted successfully`);
    } catch (error) {
      const typeName = type === 'sound-effect' ? 'sound effect' : 'music';
      toast.error(`Failed to delete ${typeName}`);
      console.error('Delete error:', error);
    }
  };

  // Repopulate the (lifted) prompt with this asset's prompt and scroll/focus
  // the prompt widget so the user can tweak and regenerate. Purely
  // client-side — no new API call.
  const handleRegenerate = (prompt: string) => {
    setPromptValue(prompt);
    promptBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    requestAnimationFrame(() => {
      promptBoxRef.current?.querySelector('textarea')?.focus();
    });
  };

  // Pagination handlers for explore tab
  const handleExploreNextPage = () => {
    if (featuredResponse?.next_page_token) {
      const newTokens = [...explorePageTokens];
      if (newTokens.length <= exploreCurrentPage + 1) {
        newTokens.push(featuredResponse.next_page_token);
        setExplorePageTokens(newTokens);
      }
      setExploreCurrentPage(exploreCurrentPage + 1);
    }
  };

  const handleExplorePrevPage = () => {
    if (exploreCurrentPage > 0) {
      setExploreCurrentPage(exploreCurrentPage - 1);
    }
  };

  // Pagination handlers for my-assets tab
  const handleMyAssetsNextPage = () => {
    if (myAssetsResponse?.next_page_token) {
      const newTokens = [...myAssetsPageTokens];
      if (newTokens.length <= myAssetsCurrentPage + 1) {
        newTokens.push(myAssetsResponse.next_page_token);
        setMyAssetsPageTokens(newTokens);
      }
      setMyAssetsCurrentPage(myAssetsCurrentPage + 1);
    }
  };

  const handleMyAssetsPrevPage = () => {
    if (myAssetsCurrentPage > 0) {
      setMyAssetsCurrentPage(myAssetsCurrentPage - 1);
    }
  };

  // "My Effects"/"My Music" count badge reuses the `count` the assets list
  // query already returns — no extra API call needed for it.
  const myAssetsCount = myAssetsResponse?.count || 0;

  const TABS: { id: TabType; label: string; sparkle?: boolean }[] = [
    { id: 'explore', label: 'Explore', sparkle: true },
    { id: 'my-assets', label: type === 'sound-effect' ? 'My Effects' : 'My Music' },
  ];

  // Helper variables for display text
  const assetTypeName = type === 'sound-effect' ? 'sound effects' : 'music';
  const emptyMyAssetsDescription = `Start creating amazing ${assetTypeName} using the prompt box below. Your generated assets will appear here.`;

  // Extract data from responses
  const featuredAssets = featuredResponse?.data || [];
  const myAssets = myAssetsResponse?.data || [];

  return (
    <div style={{ maxWidth: 1120, width: '100%', margin: '0 auto', padding: isMobile ? '16px 16px 60px' : '26px 40px 60px', boxSizing: 'border-box' }}>
      {/* ── Title ── */}
      <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: C.n900, letterSpacing: '-.02em', marginBottom: 14 }}>
        {title}
      </h1>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${C.n200}`, marginBottom: 18, overflowX: 'auto' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => handleTabChange(t.id)}
            style={{
              padding: '11px 4px',
              marginRight: 20,
              border: 'none',
              background: 'transparent',
              fontSize: 14,
              fontWeight: activeTab === t.id ? 700 : 500,
              color: activeTab === t.id ? C.n900 : C.n400,
              cursor: 'pointer',
              borderBottom: `2px solid ${activeTab === t.id ? C.n900 : 'transparent'}`,
              marginBottom: -1,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {t.sparkle && <span style={{ fontSize: 12 }}>✦</span>}
            {t.label}
            {t.id === 'my-assets' && myAssetsCount > 0 && (
              <span
                style={{
                  background: activeTab === t.id ? C.n900 : C.n200,
                  color: activeTab === t.id ? C.w : C.n600,
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '1px 7px',
                }}
              >
                {myAssetsCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Suggestion chips + prompt widget (inline, under the tab bar) ── */}
      <div ref={promptBoxRef} style={{ maxWidth: 760, margin: '0 auto 26px' }}>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          {SUGGESTION_CHIPS[type].map(chip => (
            <button
              key={chip}
              onClick={() => setPromptValue(chip)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 13px',
                background: C.w,
                border: `1px solid ${C.n200}`,
                borderRadius: 999,
                fontSize: isMobile ? 11.5 : 12.5,
                fontWeight: 600,
                color: C.n700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.n500} strokeWidth="2" strokeLinecap="round">
                <path d="M12 3l1.9 5.8L20 10l-6.1 1.2L12 17l-1.9-5.8L4 10l6.1-1.2z" />
              </svg>
              {chip}
            </button>
          ))}
        </div>
        <AudioPromptBox
          type={type}
          onGenerate={handleGenerate}
          isGenerating={isGenerating}
          value={promptValue}
          onChange={setPromptValue}
        />
      </div>

      {/* ── Search ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: isMobile ? '100%' : 200, position: 'relative', maxWidth: isMobile ? 'none' : 340, marginLeft: isMobile ? 0 : 'auto' }}>
          <Search
            size={15}
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.n400, pointerEvents: 'none' }}
          />
          <input
            type="text"
            placeholder={`Search ${assetTypeName}…`}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 34px',
              border: `1px solid ${C.n200}`,
              borderRadius: 999,
              fontSize: 13,
              outline: 'none',
              color: C.n900,
              boxSizing: 'border-box',
              background: C.w,
            }}
          />
        </div>
      </div>

      {/* ── Tab content ── */}
      {activeTab === 'explore' && (
        <>
          <AudioAssetsList
            assets={featuredAssets}
            isLoading={featuredLoading}
            isFetching={featuredFetching}
            error={featuredError}
            showDelete={false}
            onRegenerate={handleRegenerate}
            emptyStateConfig={{
              title: `No featured ${assetTypeName} yet`,
              description: `Featured ${assetTypeName} will appear here once they are uploaded.`
            }}
          />
          {featuredAssets.length > 0 && (
            <Pagination
              hasNextPage={!!featuredResponse?.next_page_token}
              hasPrevPage={exploreCurrentPage > 0}
              onNextPage={handleExploreNextPage}
              onPrevPage={handleExplorePrevPage}
              isLoading={featuredFetching}
              currentPage={exploreCurrentPage + 1}
            />
          )}
          {type === 'music' && (
            <div style={{ fontSize: 11.5, color: C.n400, textAlign: 'center', marginTop: 22 }}>
              Built in partnership with artists, labels, and publishers. For downloading and commercial use,{' '}
              <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>upgrade now</span>.
            </div>
          )}
        </>
      )}

      {activeTab === 'my-assets' && (
        <>
          <AudioAssetsList
            assets={myAssets}
            isLoading={myLoading}
            isFetching={myFetching}
            error={myError}
            showDelete={true}
            onRegenerate={handleRegenerate}
            emptyStateConfig={{
              title: `No ${assetTypeName} yet`,
              description: emptyMyAssetsDescription
            }}
            onDelete={handleDelete}
          />
          {myAssets.length > 0 && (
            <Pagination
              hasNextPage={!!myAssetsResponse?.next_page_token}
              hasPrevPage={myAssetsCurrentPage > 0}
              onNextPage={handleMyAssetsNextPage}
              onPrevPage={handleMyAssetsPrevPage}
              isLoading={myFetching}
              currentPage={myAssetsCurrentPage + 1}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AudioAssets;
